import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import InstallPrompt from './components/InstallPrompt'
import IosInstallHint from './components/IosInstallHint'
import BuildBadge from './components/BuildBadge'
import UpdateBanner from './components/UpdateBanner'
import { AuthProvider } from './lib/auth'
import { TenantProvider } from './lib/tenant'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { queryClient, queryPersister } from './lib/queryClient'
import './styles/global.css'

// Observability — lazy-loaded AND auth-gated. Only init Sentry/PostHog after
// the user is actually signed in. Saves ~120KB download + init time on the
// public sign-in page where there's no user to track anyway.
let obsBooted = false
function bootObservability() {
  if (obsBooted) return
  obsBooted = true
  if (import.meta.env.VITE_SENTRY_DSN) {
    import('@sentry/react').then(Sentry => {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
        tracesSampleRate: 0.2,
        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 1.0,
        integrations: [],
      })
    }).catch(e => console.warn('[obs] Sentry init failed', e))
  }
  if (import.meta.env.VITE_POSTHOG_KEY) {
    import('posthog-js').then(({ default: posthog }: any) => {
      posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
      })
    }).catch(e => console.warn('[obs] PostHog init failed', e))
  }
}
// Triggered by auth.tsx once a user is signed in.
if (typeof window !== 'undefined') {
  window.addEventListener('ss:user-signed-in', () => bootObservability())
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        // Hydrate query cache from localStorage so the next page-load shows
        // last-known data instantly while we silently revalidate. This is
        // the difference between "Loading…" on every cold boot and the
        // page being there before the user can blink.
        persistOptions={{ persister: queryPersister!, maxAge: 1000 * 60 * 60 * 24 }}
      >
        <BrowserRouter>
          <AuthProvider>
            <TenantProvider>
              <App />
              <InstallPrompt />
              <IosInstallHint />
              <UpdateBanner />
              <BuildBadge />
            </TenantProvider>
          </AuthProvider>
        </BrowserRouter>
      </PersistQueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
)

// Signal the pre-React splash (in index.html) that the app has mounted, so
// it can fade itself out. Defer one paint so React has actually rendered.
requestAnimationFrame(() => {
  setTimeout(() => window.dispatchEvent(new CustomEvent('ss:mounted')), 200)
})

// Removed: bootObservability() now waits for the 'ss:user-signed-in' event
// dispatched by auth.tsx, so we don't load Sentry/PostHog on the public
// sign-in page where there's no user to track.

// Service worker update flow. Previous version did `window.location.reload()`
// the moment a new SW took control — visible flash that interrupted the user
// mid-tap. New behaviour: dispatch a custom event so a non-intrusive toast
// can offer "Update available — refresh". Falls back to auto-reload if the
// user hasn't interacted (i.e. the app is idle and a flash is harmless).
//
// We only show the toast for genuine UPDATES — not the first time a SW
// claims a page. Track in localStorage whether we've seen a controller
// before; if we haven't, the first controllerchange is just initial install.
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  let reloading = false
  let lastInteraction = Date.now()
  ;['click', 'keydown', 'touchstart', 'pointerdown'].forEach(ev => {
    document.addEventListener(ev, () => { lastInteraction = Date.now() }, { passive: true })
  })
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return
    // First-install case — no previous SW, this is just the worker claiming
    // the page for the first time. Mark and stay quiet.
    try {
      if (!localStorage.getItem('ss.sw-seen')) {
        localStorage.setItem('ss.sw-seen', '1')
        return
      }
    } catch {}
    reloading = true
    // If user has been idle >20s, refresh quietly. Otherwise surface a toast
    // and let them tap when ready — no work lost mid-flow.
    const idle = Date.now() - lastInteraction > 20_000
    if (idle) setTimeout(() => window.location.reload(), 150)
    else window.dispatchEvent(new CustomEvent('ss:sw-update-available'))
  })
  // Mark "seen" for users who already had a SW running when this code
  // shipped — prevents a spurious toast on their next visit.
  if (navigator.serviceWorker.controller) {
    try { localStorage.setItem('ss.sw-seen', '1') } catch {}
  }
  // Best-effort: also poll for updates on focus, so if the tab has been
  // open for hours the user picks up the new bundle when they come back.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      navigator.serviceWorker.getRegistration().then(reg => reg?.update().catch(() => {}))
    }
  })
}
